import { BarChart3, BookOpen, Search, Target, Users } from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useProgressiveExamples } from '../hooks/useProgressiveContent';
import { createSearchService } from '../lib/searchService';
import { useNews } from '../hooks/useContent';
import { audienceLabels, complexityLabels, goalLabels } from '../lib/router';
import { TagsSidebar } from '../components/TagsSidebar';

export function ExamplesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGoal, setSelectedGoal] = useState<string>('all');
  const [selectedAudience, setSelectedAudience] = useState<string>('all');
  const [selectedComplexity, setSelectedComplexity] = useState<string>('all');
  
  const { examples, loading, error } = useProgressiveExamples();
  const { posts } = useNews();
  
  // Create search service for filtering
  const searchService = createSearchService(posts, examples);
  
  // Filter examples using the unified search service
  const filteredExamples = searchService.filterExamples({
    query: searchQuery,
    goals: selectedGoal && selectedGoal !== 'all' ? [selectedGoal] : undefined,
    audiences: selectedAudience && selectedAudience !== 'all' ? [selectedAudience] : undefined,
    complexities: selectedComplexity && selectedComplexity !== 'all' ? [selectedComplexity] : undefined
  });

  // Get filter options
  const filterOptions = searchService.getFilterOptions();
  const goals = filterOptions.goals;
  const audiences = filterOptions.audiences;
  const complexities = filterOptions.complexities;

  if (error) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Unable to load examples</h1>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Prompt Engineering Examples - Proven Techniques | Promptliteracy.ai</title>
        <meta name="description" content="Discover proven prompt engineering patterns and techniques with step-by-step explanations. Learn effective AI communication through real-world examples for students, educators, and professionals." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Prompt Engineering Examples - Proven Techniques | Promptliteracy.ai" />
        <meta property="og:description" content="Discover proven prompt engineering patterns and techniques with step-by-step explanations. Learn effective AI communication through real-world examples." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/examples" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Prompt Engineering Examples - Proven Techniques" />
        <meta name="twitter:description" content="Learn effective AI communication through proven prompt engineering patterns and real-world examples." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="prompt engineering examples, AI prompt templates, prompt patterns, AI communication examples, prompt engineering techniques, AI prompting guide" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/examples" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Prompt Engineering Examples",
            "description": "Collection of proven prompt engineering patterns and techniques",
            "url": "https://promptliteracy.ai/examples",
            "mainEntity": {
              "@type": "ItemList",
              "name": "Prompt Engineering Examples",
              "description": "Step-by-step prompt engineering tutorials and examples"
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="h-8 w-8" />
              <h1 className="text-4xl font-bold">Prompt Examples</h1>
            </div>
            <p className="text-xl opacity-90 mb-8">
              Proven patterns and techniques with step-by-step explanations
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span>Step-by-step guides</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                <span>Real-world patterns</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/5">
        <div className="container">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Filters */}
              <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Find Examples</h2>
          <p className="text-muted-foreground mb-6">
            Filter by goal, audience, or complexity level
          </p>

          {/* Search and Filters */}
          <div className="mt-6 flex flex-col gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search examples..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white border-slate-300">
                  <Target className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Goals" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border border-border shadow-lg">
                  <SelectItem value="all" className="link-underline">All Goals</SelectItem>
                  {goals.filter(goal => goal && goal.trim() !== '').map((goal) => (
                    <SelectItem key={goal} value={goal} className="link-underline">
                      {goalLabels[goal] || goal}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedAudience} onValueChange={setSelectedAudience}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white border-slate-300">
                  <Users className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Audiences" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border border-border shadow-lg">
                  <SelectItem value="all" className="link-underline">All Audiences</SelectItem>
                  {audiences.filter(audience => audience && audience.trim() !== '').map((audience) => (
                    <SelectItem key={audience} value={audience} className="link-underline">
                      {audienceLabels[audience] || audience}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedComplexity} onValueChange={setSelectedComplexity}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white border-slate-300">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border border-border shadow-lg">
                  <SelectItem value="all" className="link-underline">All Levels</SelectItem>
                  {complexities.filter(complexity => complexity && complexity.trim() !== '').map((complexity) => (
                    <SelectItem key={complexity} value={complexity} className="link-underline">
                      {complexityLabels[complexity] || complexity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-muted-foreground">
            {loading ? 'Loading...' : `${filteredExamples.length} example${filteredExamples.length !== 1 ? 's' : ''} found`}
          </div>
        </div>

        {/* Examples Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="flex gap-2 mb-2">
                    <div className="h-5 bg-muted rounded w-16" />
                    <div className="h-5 bg-muted rounded w-20" />
                  </div>
                  <div className="h-4 bg-muted rounded w-3/4" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded" />
                    <div className="h-3 bg-muted rounded w-4/5" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredExamples.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExamples.map((example) => (
              <Link key={example.slug} to={`/examples/${example.slug}`} className="block group">
                <Card className="card-slate hover-lift border-t-4 border-t-primary/30 hover:border-t-primary transition-all hover:scale-105 cursor-pointer card-accent">
                  <CardHeader>
                    <CardTitle className="text-lg leading-tight text-primary mb-3">
                      {example.title}
                    </CardTitle>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      <Link 
                        to={`/tag/${example.complexity}`} 
                        className="inline-block px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary border border-primary/20 rounded transition-colors whitespace-nowrap"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        {complexityLabels[example.complexity]}
                      </Link>
                      <Link 
                        to={`/tag/${example.audience}`} 
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="inline-block px-2 py-1 text-xs bg-secondary/10 text-secondary hover:bg-secondary/20 hover:text-primary border border-secondary/20 rounded transition-colors whitespace-nowrap"
                      >
                        {audienceLabels[example.audience]}
                      </Link>
                      <Link 
                        to={`/tag/${example.goal}`} 
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="inline-block px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary border border-primary/20 rounded transition-colors whitespace-nowrap"
                      >
                        {goalLabels[example.goal]}
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      {example.context}
                    </p>
                    
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-3 w-3 text-primary" />
                        <p className="text-xs font-medium text-primary">
                          Prompt Preview:
                        </p>
                      </div>
                      <code className="text-xs text-muted-foreground leading-relaxed block">
                        {example.prompt_template.split('\n')[0].substring(0, 80)}...
                      </code>
                    </div>

                    {example.tags && example.tags.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-1.5 sm:gap-2">
                        {example.tags.map((tag) => (
                          <Link 
                            key={tag}
                            to={`/tag/${tag}`}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            className="inline-block px-2 py-0.5 text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 rounded border border-slate-200 transition-colors whitespace-nowrap"
                          >
                            {tag.replace('-', ' ')}
                          </Link>
                        ))}
                      </div>
                    )}

                    <Button variant="outline" size="sm" className="w-full border-slate-300 btn-blue-hover transition-colors">
                      <BookOpen className="mr-2 h-4 w-4" />
                      View Full Example
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No examples found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search terms or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedGoal('all');
                setSelectedAudience('all');
                setSelectedComplexity('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <TagsSidebar
            tags={[
              ...goals.map(goal => ({ tag: goal, count: examples.filter(ex => ex.goal === goal).length, type: 'goal' as const })),
              ...audiences.map(audience => ({ tag: audience, count: examples.filter(ex => ex.audience === audience).length, type: 'audience' as const })),
              ...complexities.map(complexity => ({ tag: complexity, count: examples.filter(ex => ex.complexity === complexity).length, type: 'complexity' as const }))
            ]}
            title="Categories"
            maxTags={12}
          />
        </aside>
          </div>
        </div>
      </section>
    </>
  );
}